import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useClipboard,
  VStack,
} from "@chakra-ui/react";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { useContext, useEffect } from "react";
import { KYDAdminEvent } from "@common/interfaces/KYDEvent";
import { useForm } from "react-hook-form";
import { KYDOrganizationEventLink } from "@common/interfaces/KYDOrganization";
import PermissionSelector from "./Permissions";

export const LinkOrganizationDialogContext = createDialogContext();

const LinkOrganizationDialog = ({
  editingLinkedOrganization,
  isLoading = false,
  onDelete,
  onSave,
  onClose,
}: {
  kydEvent?: KYDAdminEvent;
  isLoading?: boolean;
  editingLinkedOrganization?: KYDOrganizationEventLink;
  onSave?: (values: Partial<KYDOrganizationEventLink>) => Promise<void>;
  onDelete: (item: KYDOrganizationEventLink) => Promise<void>;
  onClose: () => void;
}) => {
  const { isOpen, close } = useContext(LinkOrganizationDialogContext);
  const { onCopy, hasCopied } = useClipboard(
    editingLinkedOrganization?.organization_id
  );
  const {
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { isValid, isDirty },
  } = useForm({
    mode: "all",
    criteriaMode: "all",
    values: {
      ...editingLinkedOrganization,
      organization_id: editingLinkedOrganization
        ? editingLinkedOrganization.organization_id
        : "",
      scopes: editingLinkedOrganization ? editingLinkedOrganization.scopes : [],
    },
  });

  const scopes = watch("scopes");
  const _onSubmit = async (values: Partial<KYDOrganizationEventLink>) => {
    try {
      await onSave(values);
    } catch (e) {}
  };

  const _onClose = () => {
    reset();
    close();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      size="xl"
      isCentered
      onClose={() => {
        _onClose();
      }}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader paddingTop={4}>
          <Text w="full">
            {editingLinkedOrganization
              ? `View link with ${editingLinkedOrganization.organization?.name}`
              : "Share event with organization"}
          </Text>
        </ModalHeader>
        <ModalBody>
          <VStack w="full" spacing={2}>
            <VStack mb={4} w="full">
              <Text fontWeight={"medium"} w="full">
                {editingLinkedOrganization ? "Organization" : "Organization ID"}
              </Text>
              {editingLinkedOrganization ? null : (
                <Text fontSize={"xs"}>
                  The organization ID can be found by the organization you want
                  to share with in KYD platform {">"} Settings on the left
                  sidebar
                </Text>
              )}
              {editingLinkedOrganization ? (
                <>
                  <Input
                    value={editingLinkedOrganization.organization?.name}
                    isDisabled={!!editingLinkedOrganization}
                    readOnly
                    placeholder="Organization ID"
                  />
                  <Text fontSize={"xs"} w="full" opacity={0.5} onClick={onCopy}>
                    {editingLinkedOrganization.organization_id}
                  </Text>
                </>
              ) : (
                <Input
                  {...register("organization_id", {
                    required: true,
                  })}
                  isDisabled={isLoading}
                  placeholder="Organization ID"
                />
              )}
            </VStack>
            <PermissionSelector
              mode="organization"
              scopes={scopes}
              isDisabled={isLoading}
              onChange={(values) =>
                setValue("scopes", values.scopes, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr="4" isDisabled={isLoading} onClick={_onClose}>
            Close
          </Button>
          {editingLinkedOrganization ? (
            <Button
              mr="4"
              bg="red.500"
              color="white"
              isDisabled={isLoading || !isValid}
              isLoading={isLoading}
              onClick={() => {
                onDelete(editingLinkedOrganization);
                _onClose();
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button
            bg="green.500"
            color="white"
            isDisabled={isLoading || !isValid || !isDirty}
            isLoading={isLoading}
            onClick={handleSubmit(_onSubmit)}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export const withLinkOrganizationDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={LinkOrganizationDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default LinkOrganizationDialog;
